import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import BaseInput from '../../form/BaseInput.jsx';
import BaseButton from '../../form/BaseButton.jsx';
import IconAdd from '../../images/IconAdd.jsx';
import BaseDropdown from '../../form/BaseDropdown.jsx';

dayjs.extend(utc);

const ProjectMilestoneReportAddEdit = ({ addVisible, setAddVisible, onSave, type, initialData = {} }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        date: null,
        type: ''
    });

    const milestoneTypes = [
        { label: 'To-Do', value: 'to-do' },
        { label: 'In Progress', value: 'in-progress' },
        { label: 'Completed', value: 'completed' }
    ];

    const [error, setError] = useState('');

    useEffect(() => {
        if (addVisible) {
            setFormData({
                title: initialData.title || '',
                description: initialData.description || '',
                date: initialData.date ? dayjs(initialData.date).toDate() : null,
                type: initialData.type
            });
            setError('');
        }
    }, [addVisible, initialData.title, initialData.description, initialData.date, initialData.type]);

    const hideDialog = () => {
        setAddVisible(false);
    };

    const handleSave = () => {
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }

        onSave({
            id: initialData.id || Date.now().toString(),
            title: formData.title,
            description: formData.description || '',
            date: formData.date ? dayjs(formData.date).utc().format() : null,
            type: formData.type
        });

        hideDialog();
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
        if (field === 'title' && value.trim()) setError('');
    };

    const isEditMode = !!initialData.id;
    const singularType = type?.slice(0, -1);

    return (
        <Dialog
            className='rounded-4xl shadow-deep-red-shadow'
            visible={addVisible}
            style={{ width: '400px' }}
            breakpoints={{ '420px': '320px' }}
            onHide={hideDialog}
        >
            <div className='p-2 pt-0'>
                <div className='flex items-center mb-4 gap-4'>
                    <div className='bg-theme1-lightBtns rounded-full p-2'>
                        <IconAdd className=' fill-white rounded-full w-6 h-6' />
                    </div>

                    <div className='text-theme1-title2 text-xl font-bold'>
                        {isEditMode ? `Edit ${singularType}` : `Add ${singularType}`}
                    </div>
                </div>
                <BaseInput
                    id='milestone-title'
                    name='title'
                    type='text'
                    value={formData.title}
                    setValue={(value) => handleChange('title', value)}
                    placeholder={`${singularType} title`}
                />
                {error && <p className='text-red-500 text-sm'>{error}</p>}
                <BaseInput
                    id='milestone-description'
                    name='description'
                    type='text'
                    value={formData.description}
                    setValue={(value) => handleChange('description', value)}
                    placeholder={`${singularType} description`}
                    className='mt-4'
                />
                <div className='mb-4 mt-4'>
                    <Calendar
                        id='milestone-date'
                        value={formData.date}
                        onChange={(e) => handleChange('date', e.value)}
                        placeholder={`${singularType} date`}
                        dateFormat='dd M yy'
                        className='w-full text-theme1-text1 rounded-2xl border border-theme1-text3 text-base'
                        inputClassName='custom-calendar-input'
                    />
                </div>
                {singularType.toLowerCase() === 'milestone' && (
                    <div className='mb-4 mt-4'>
                        <BaseDropdown
                            name='type'
                            value={formData.type}
                            options={milestoneTypes}
                            placeholder={`${singularType} status`}
                            setValue={(value) => handleChange('type', value)}
                            className='w-full text-theme1-text1 rounded-2xl text-base'
                        />
                    </div>
                )}
                <div className='flex justify-end gap-2'>
                    <BaseButton className='btn-red-outline' textValue='Cancel' onClick={hideDialog} />
                    <BaseButton className='btn-primary' textValue='Save' onClick={handleSave} />
                </div>
            </div>
        </Dialog>
    );
};

export default ProjectMilestoneReportAddEdit;
